import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tier hierarchy
const TIER_LEVELS = {
  "free": 0,
  "watch-all": 1,
  "silver": 2,
  "gold": 3,
  "admin": 999 // Admin has highest access
};

const TIER_NAMES = {
  "free": "Free",
  "watch-all": "Watch All Videos",
  "silver": "Silver",
  "gold": "Gold",
  "admin": "Admin"
};

const TIER_PRICES = {
  "free": 0,
  "watch-all": 2.99,
  "silver": 9.99,
  "gold": 49.99,
  "admin": 0
};

const MembershipContext = createContext(null);

export const MembershipProvider = ({ children }) => {
  const [membership, setMembership] = useState({
    tier: 'free',
    tierLevel: 0,
    planName: 'Free',
    hasChecked: false,
    isAdmin: false
  });
  const [userEmail, setUserEmail] = useState(null);

  // Check if user is admin from localStorage token
  const checkIsAdmin = () => {
    try {
      // Check auth_user first (most reliable)
      const authUser = localStorage.getItem('auth_user');
      if (authUser) {
        const userData = JSON.parse(authUser);
        return userData.role === 'admin';
      }
      
      // Fallback: check JWT token
      const token = localStorage.getItem('auth_token');
      if (token) {
        // Decode JWT to check role (simple base64 decode of payload)
        const payload = token.split('.')[1];
        if (payload) {
          const decoded = JSON.parse(atob(payload));
          return decoded.role === 'admin';
        }
      }
    } catch (e) {
      console.log('Token decode error:', e);
    }
    return false;
  };

  // Check membership on mount and when email changes
  useEffect(() => {
    const isAdmin = checkIsAdmin();
    
    if (isAdmin) {
      // Admin has full access
      setMembership({
        tier: 'admin',
        tierLevel: 999,
        planName: 'Admin',
        hasChecked: true,
        isAdmin: true
      });
      return;
    }

    const storedEmail = localStorage.getItem('userEmail');
    if (storedEmail) {
      setUserEmail(storedEmail);
      checkMembership(storedEmail);
    } else {
      setMembership(prev => ({ ...prev, hasChecked: true }));
    }
  }, []);

  const checkMembership = async (email) => {
    // First check if admin
    const isAdmin = checkIsAdmin();
    if (isAdmin) {
      setMembership({
        tier: 'admin',
        tierLevel: 999,
        planName: 'Admin',
        hasChecked: true,
        isAdmin: true
      });
      return;
    }

    if (!email) {
      setMembership({ tier: 'free', tierLevel: 0, planName: 'Free', hasChecked: true, isAdmin: false });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/membership/check?email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setMembership({
          tier: data.tier,
          tierLevel: data.tier_level,
          planName: data.plan_name || TIER_NAMES[data.tier],
          canAccess: data.can_access,
          hasMembership: data.has_membership,
          hasChecked: true,
          isAdmin: false
        });
      }
    } catch (error) {
      console.error('Failed to check membership:', error);
      setMembership(prev => ({ ...prev, hasChecked: true }));
    }
  };

  const setUser = (email) => {
    setUserEmail(email);
    if (email) {
      localStorage.setItem('userEmail', email);
      checkMembership(email);
    } else {
      localStorage.removeItem('userEmail');
      // Check if still admin
      const isAdmin = checkIsAdmin();
      if (isAdmin) {
        setMembership({ tier: 'admin', tierLevel: 999, planName: 'Admin', hasChecked: true, isAdmin: true });
      } else {
        setMembership({ tier: 'free', tierLevel: 0, planName: 'Free', hasChecked: true, isAdmin: false });
      }
    }
  };

  // Refresh admin status (call this after login/logout)
  const refreshAdminStatus = () => {
    const isAdmin = checkIsAdmin();
    if (isAdmin) {
      setMembership({
        tier: 'admin',
        tierLevel: 999,
        planName: 'Admin',
        hasChecked: true,
        isAdmin: true
      });
      return true;
    }
    return false;
  };

  const canAccess = (requiredTier) => {
    // Admin can access everything
    if (membership.isAdmin || membership.tier === 'admin') {
      return true;
    }
    const userLevel = TIER_LEVELS[membership.tier] || 0;
    const requiredLevel = TIER_LEVELS[requiredTier] || 0;
    return userLevel >= requiredLevel;
  };

  const getUpgradeTier = (requiredTier) => {
    if (canAccess(requiredTier)) return null;
    return {
      tier: requiredTier,
      name: TIER_NAMES[requiredTier],
      price: TIER_PRICES[requiredTier]
    };
  };

  return (
    <MembershipContext.Provider value={{
      membership,
      userEmail,
      setUser,
      checkMembership,
      refreshAdminStatus,
      canAccess,
      getUpgradeTier,
      TIER_NAMES,
      TIER_PRICES,
      TIER_LEVELS
    }}>
      {children}
    </MembershipContext.Provider>
  );
};

export const useMembership = () => {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within a MembershipProvider');
  }
  return context;
};

export default MembershipContext;
